import battleLayoutProjection from "./data/battleLayoutProjection.generated.json";

export const BATTLE_PROJECTION_MODES = Object.freeze({
  GAME: "game",
  TACTICAL: "tactical",
});

export const DEFAULT_BATTLE_PROJECTION_MODE = BATTLE_PROJECTION_MODES.TACTICAL;

const DEFAULT_GRID = Object.freeze({
  rowCount: Number(battleLayoutProjection?.grid?.rowCount || 32),
  columnCount: Number(battleLayoutProjection?.grid?.columnCount || 18),
});

const DEFAULT_WORLD = Object.freeze({
  sceneScale: 15,
  battlefieldHalfExtent: 800,
  battleFieldIndex: 3,
  engineUnitToWorld: (2 * 800 / (DEFAULT_GRID.rowCount / 2)) / 15,
  offsetGridZ: ((3 - 1) * 0.5 * (DEFAULT_GRID.rowCount / 2)),
});

const DEFAULT_PRESETS = battleLayoutProjection?.projectionPresets || {};
const UI_BATTLEFIELD_SPACING_SCALE = 2;
const ENERGY_GUARD_SPELL_GID = 100925448;
const BISON_AURA_SPELL_GIDS = new Set([100925448]);
const TITAN_WTYPE_EMPRESS = 20;
const TITAN_WTYPE_BISON = 21;
const OFFICER_SKILL_ID_MIN = 100859900;
const OFFICER_SKILL_ID_MAX = 100860050;
const HERO_SPELL_TO_OFFICER_SKILL = Object.freeze({
  50397431: 100859990,
  50397503: 100859990,
  50462842: 100859982,
  50462843: 100859986,
  50462844: 100859984,
});

const asNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const clampPercent = (value) => Math.max(1, Math.min(99, asNumber(value, 50)));
const clampMarkerY = (value) => Math.max(7, Math.min(93, asNumber(value, 50)));

const normalizePositiveInt = (value) => {
  const id = Math.trunc(asNumber(value, NaN));
  return Number.isFinite(id) && id > 0 ? id : null;
};

const isOfficerSkillId = (value) => {
  const id = normalizePositiveInt(value);
  return Boolean(id && id >= OFFICER_SKILL_ID_MIN && id <= OFFICER_SKILL_ID_MAX);
};

const resolveOfficerSourceSkillId = (event = {}) => {
  const directCandidates = [
    event?.skillGid,
    event?.inferredSkillGid,
    event?.sourceId,
    event?.sourceGid,
    event?.spellGid,
  ];
  for (const rawId of directCandidates) {
    const id = normalizePositiveInt(rawId);
    if (!id) continue;
    if (isOfficerSkillId(id)) return id;
    if (HERO_SPELL_TO_OFFICER_SKILL[id]) return HERO_SPELL_TO_OFFICER_SKILL[id];
  }
  return null;
};

const buildOfficerSourceMarker = ({ skillId, side, targetPoint }) => ({
  type: "officer",
  skillId,
  side: String(side || "attacker") === "defender" ? "defender" : "attacker",
  x: 4,
  y: clampMarkerY(targetPoint?.y),
});

const humanizeToken = (raw) => String(raw || "")
  .trim()
  .split(/[_\s]+/)
  .filter(Boolean)
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
  .join(" ");

const addStackStatus = (statuses, status) => {
  if (!Array.isArray(statuses) || !status?.key) return;
  const key = String(status.key);
  if (statuses.some((entry) => String(entry?.key || "") === key)) return;
  statuses.push({
    key,
    label: String(status.label || humanizeToken(key)),
    kind: String(status.kind || "neutral"),
    title: String(status.title || status.label || humanizeToken(key)),
  });
};

const mergeStackStatuses = (target, statuses) => {
  if (!target || !Array.isArray(statuses) || !statuses.length) return;
  if (!Array.isArray(target.statuses)) target.statuses = [];
  statuses.forEach((status) => addStackStatus(target.statuses, status));
};

const applyUiSpacing = (point) => {
  const x = asNumber(point?.x, 50);
  const y = asNumber(point?.y, 50);
  return {
    x: clampPercent(50 + ((x - 50) * UI_BATTLEFIELD_SPACING_SCALE)),
    y: clampPercent(50 + ((y - 50) * UI_BATTLEFIELD_SPACING_SCALE)),
  };
};

const rotate2D = (x, z, degrees) => {
  const rad = (asNumber(degrees, 0) * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return {
    x: (x * cos) - (z * sin),
    z: (x * sin) + (z * cos),
  };
};

const resolveGrid = (traceMeta) => ({
  rowCount: Math.max(1, asNumber(traceMeta?.grid?.rowCount, DEFAULT_GRID.rowCount)),
  columnCount: Math.max(1, asNumber(traceMeta?.grid?.columnCount, DEFAULT_GRID.columnCount)),
});

const resolveWorldTransform = (traceMeta, grid) => {
  const raw = traceMeta?.worldTransform || {};
  const sceneScale = Math.max(1, asNumber(raw.sceneScale, DEFAULT_WORLD.sceneScale));
  const battlefieldHalfExtent = Math.max(1, asNumber(raw.battlefieldHalfExtent, DEFAULT_WORLD.battlefieldHalfExtent));
  const engineUnitToWorld = asNumber(
    raw.engineUnitToWorld,
    (2 * battlefieldHalfExtent / (grid.rowCount / 2)) / sceneScale,
  );
  const offsetGridZ = asNumber(raw.offsetGridZ, DEFAULT_WORLD.offsetGridZ);
  return {
    sceneScale,
    battlefieldHalfExtent,
    battleFieldIndex: Math.max(1, Math.floor(asNumber(raw.battleFieldIndex, DEFAULT_WORLD.battleFieldIndex))),
    engineUnitToWorld,
    offsetGridZ,
  };
};

const resolvePreset = (battleType, traceMeta) => {
  if (traceMeta?.projectionPreset && typeof traceMeta.projectionPreset === "object") {
    return traceMeta.projectionPreset;
  }
  const key = String(traceMeta?.battleType || battleType || "").trim().toLowerCase();
  return DEFAULT_PRESETS[key] || DEFAULT_PRESETS.uranium_mine || {
    id: "default_projection",
    battleType: key || "uranium_mine",
    rootRotationDeg: 30,
    offsetGridZ: DEFAULT_WORLD.offsetGridZ,
    camera: { tiltDeg: 50, yawDeg: -10, fov: 32 },
    calibration: { xScale: 1, yScale: 1, offsetX: 0, offsetY: 0 },
  };
};

const engineToWorldXZ = (engineX, engineY, world, grid, preset) => {
  const colHalf = grid.columnCount / 2;
  const rowHalf = grid.rowCount / 2;
  const offsetGridZ = asNumber(preset?.offsetGridZ, world.offsetGridZ);
  return {
    x: (asNumber(engineX, colHalf) - colHalf) * world.engineUnitToWorld,
    z: (offsetGridZ + asNumber(engineY, rowHalf) - rowHalf) * world.engineUnitToWorld,
  };
};

const projectWorldPoint = (worldPoint, preset, bounds) => {
  const rotated = rotate2D(worldPoint.x, worldPoint.z, asNumber(preset?.rootRotationDeg, 0));
  const cameraRotated = rotate2D(rotated.x, rotated.z, asNumber(preset?.camera?.yawDeg, 0));
  const tiltRad = (asNumber(preset?.camera?.tiltDeg, 50) * Math.PI) / 180;
  const calibration = preset?.calibration || {};
  const x = (cameraRotated.x * asNumber(calibration.xScale, 1)) + asNumber(calibration.offsetX, 0);
  const y = (
    (cameraRotated.z * Math.cos(tiltRad) * asNumber(calibration.yScale, 1))
    - (cameraRotated.z * Math.sin(tiltRad) * 0.08)
    + asNumber(calibration.offsetY, 0)
  );

  const width = Math.max(0.00001, bounds.maxX - bounds.minX);
  const height = Math.max(0.00001, bounds.maxY - bounds.minY);
  return applyUiSpacing({
    x: ((x - bounds.minX) / width) * 100,
    y: 100 - (((y - bounds.minY) / height) * 100),
  });
};

const orientProjectedPoint = (point, projectionMode) => {
  if (projectionMode !== BATTLE_PROJECTION_MODES.GAME) return point;
  return {
    x: clampPercent(100 - asNumber(point?.x, 50)),
    y: clampPercent(100 - asNumber(point?.y, 50)),
  };
};

const createProjectionBounds = (preset, grid, world) => {
  const corners = [
    engineToWorldXZ(0.5, 0.5, world, grid, preset),
    engineToWorldXZ(grid.columnCount - 0.5, 0.5, world, grid, preset),
    engineToWorldXZ(0.5, grid.rowCount - 0.5, world, grid, preset),
    engineToWorldXZ(grid.columnCount - 0.5, grid.rowCount - 0.5, world, grid, preset),
  ];

  const projected = corners.map((corner) => {
    const rotated = rotate2D(corner.x, corner.z, asNumber(preset?.rootRotationDeg, 0));
    const cameraRotated = rotate2D(rotated.x, rotated.z, asNumber(preset?.camera?.yawDeg, 0));
    const tiltRad = (asNumber(preset?.camera?.tiltDeg, 50) * Math.PI) / 180;
    const calibration = preset?.calibration || {};
    return {
      x: (cameraRotated.x * asNumber(calibration.xScale, 1)) + asNumber(calibration.offsetX, 0),
      y: (
        (cameraRotated.z * Math.cos(tiltRad) * asNumber(calibration.yScale, 1))
        - (cameraRotated.z * Math.sin(tiltRad) * 0.08)
        + asNumber(calibration.offsetY, 0)
      ),
    };
  });

  const minX = Math.min(...projected.map((entry) => entry.x));
  const maxX = Math.max(...projected.map((entry) => entry.x));
  const minY = Math.min(...projected.map((entry) => entry.y));
  const maxY = Math.max(...projected.map((entry) => entry.y));
  const xPad = Math.max(0.01, (maxX - minX) * 0.08);
  const yPad = Math.max(0.01, (maxY - minY) * 0.08);
  return {
    minX: minX - xPad,
    maxX: maxX + xPad,
    minY: minY - yPad,
    maxY: maxY + yPad,
  };
};

const buildProjectionContext = ({ mode, battleType, traceMeta }) => {
  const projectionMode = mode === BATTLE_PROJECTION_MODES.TACTICAL
    ? BATTLE_PROJECTION_MODES.TACTICAL
    : BATTLE_PROJECTION_MODES.GAME;
  const grid = resolveGrid(traceMeta);
  const world = resolveWorldTransform(traceMeta, grid);
  const preset = resolvePreset(battleType, traceMeta);
  const bounds = createProjectionBounds(preset, grid, world);

  const projectEnginePoint = ({ x, y }) => {
    const engineX = asNumber(x, grid.columnCount / 2);
    const engineY = asNumber(y, grid.rowCount / 2);
    if (projectionMode === BATTLE_PROJECTION_MODES.TACTICAL) {
      return applyUiSpacing({
        x: (engineX / grid.columnCount) * 100,
        y: 100 - ((engineY / grid.rowCount) * 100),
      });
    }
    const worldPoint = engineToWorldXZ(engineX, engineY, world, grid, preset);
    return orientProjectedPoint(projectWorldPoint(worldPoint, preset, bounds), projectionMode);
  };

  const projectUnit = (unit) => {
    const engine = unit?.engineGrid && typeof unit.engineGrid === "object"
      ? { x: asNumber(unit.engineGrid.x, NaN), y: asNumber(unit.engineGrid.y, NaN) }
      : { x: NaN, y: NaN };

    if (!Number.isFinite(engine.x) || !Number.isFinite(engine.y)) {
      const worldPoint = unit?.worldXZ && typeof unit.worldXZ === "object"
        ? { x: asNumber(unit.worldXZ.x, 0), z: asNumber(unit.worldXZ.z, 0) }
        : null;
      if (worldPoint) {
        if (projectionMode === BATTLE_PROJECTION_MODES.TACTICAL) {
          const colHalf = grid.columnCount / 2;
          const rowHalf = grid.rowCount / 2;
          const offsetGridZ = asNumber(preset?.offsetGridZ, world.offsetGridZ);
          const engineX = (worldPoint.x / world.engineUnitToWorld) + colHalf;
          const engineY = (worldPoint.z / world.engineUnitToWorld) - offsetGridZ + rowHalf;
          return projectEnginePoint({ x: engineX, y: engineY });
        }
        return orientProjectedPoint(projectWorldPoint(worldPoint, preset, bounds), projectionMode);
      }
      return { x: 50, y: 50 };
    }

    return projectEnginePoint(engine);
  };

  return {
    mode: projectionMode,
    preset,
    grid,
    world,
    bounds,
    projectEnginePoint,
    projectUnit,
  };
};

const buildTargetLine = ({ from, to, style }) => {
  if (!from || !to) return null;
  return {
    x1: from.x,
    y1: from.y,
    x2: to.x,
    y2: to.y,
    style,
  };
};

const resolveDisplayTroopId = (unit = {}) => {
  const wtype = Math.trunc(asNumber(unit?.wtype ?? unit?.warriorType, NaN));
  if (wtype === TITAN_WTYPE_EMPRESS) return 'femaleTitan';
  if (wtype === TITAN_WTYPE_BISON) return 'maleTitan';
  const familyId = String(unit?.familyId || '');
  const rawTroopId = String(unit?.troopId || '');
  return familyId || rawTroopId;
};

export function projectFrame({
  frame,
  frameEvents = [],
  troopIds = [],
  mode = DEFAULT_BATTLE_PROJECTION_MODE,
  battleType = "uranium_mine",
  traceMeta = null,
  visualContext = null,
}) {
  const ctx = buildProjectionContext({ mode, battleType, traceMeta });
  const troopIdSet = new Set((troopIds || []).map((entry) => String(entry)));
  const units = Array.isArray(frame?.units) ? frame.units : [];
  const events = Array.isArray(frameEvents) ? frameEvents : [];
  const rawAreaEffects = Array.isArray(frame?.areaEffects) ? frame.areaEffects : [];
  const frameNumber = Math.max(0, Math.floor(asNumber(frame?.frame, 0)));

  const attackerFrameArmy = {};
  const defenderFrameArmy = {};
  const attackerFrameHp = {};
  const defenderFrameHp = {};
  const poseBuckets = new Map();
  const poseByStackId = {};
  const stackMetaById = new Map();
  const stackStatesByTroop = {};
  const stackStatesByStackId = {};
  const renderStacks = [];
  const sharedVisualContext = visualContext && typeof visualContext === "object" ? visualContext : null;
  const ensureContextMap = (key) => {
    if (!sharedVisualContext) return new Map();
    if (!(sharedVisualContext[key] instanceof Map)) {
      sharedVisualContext[key] = new Map();
    }
    return sharedVisualContext[key];
  };
  const contextLastOfficerSkillBySide = ensureContextMap("lastOfficerSkillBySide");
  const contextLastPoseByStackId = ensureContextMap("lastPoseByStackId");
  const contextLastPoseByTroopKey = ensureContextMap("lastPoseByTroopKey");
  const contextLastSourceByTargetStackId = ensureContextMap("lastSourceByTargetStackId");

  const buildStackStatuses = (unitState = {}, unit = {}) => {
    const statuses = [];
    const misfireUntilFrame = Math.max(0, Math.floor(asNumber(unitState?.misfireUntilFrame, 0)));
    if (unitState?.misfiring || misfireUntilFrame > frameNumber) {
      addStackStatus(statuses, {
        key: "misfire",
        label: "Misfire",
        kind: "negative",
        title: misfireUntilFrame > frameNumber
          ? `Misfire until frame ${misfireUntilFrame}`
          : "Misfire active",
      });
    }
    if (unitState?.banSkill) {
      addStackStatus(statuses, {
        key: "skill-ban",
        label: "Skill Ban",
        kind: "negative",
        title: "Skill casting blocked",
      });
    }
    const shieldValue = Math.max(asNumber(unitState?.shield, 0), asNumber(unitState?.overlayShield, 0));
    if (shieldValue > 0.0001) {
      addStackStatus(statuses, {
        key: "shield",
        label: "Shield",
        kind: "positive",
        title: `Shield ${Math.round(shieldValue).toLocaleString()}`,
      });
    }
    const buffSpeed = asNumber(unitState?.buffSpeed, 1);
    if (buffSpeed > 1.0001) {
      addStackStatus(statuses, {
        key: "haste",
        label: "Haste",
        kind: "positive",
        title: `Speed x${buffSpeed.toFixed(2)}`,
      });
    } else if (buffSpeed > 0 && buffSpeed < 0.9999) {
      addStackStatus(statuses, {
        key: "slow",
        label: "Slow",
        kind: "negative",
        title: `Speed x${buffSpeed.toFixed(2)}`,
      });
    }
    const hpAura = asNumber(unitState?.hpScaleAura, 0);
    if (hpAura > 0.0001) {
      addStackStatus(statuses, {
        key: "hp-aura",
        label: "HP Aura",
        kind: "aura",
        title: `HP aura +${(hpAura * 100).toFixed(1)}%`,
      });
    }
    const damageResist = asNumber(unitState?.buffDamageResist, 0);
    const damageReduce = asNumber(unitState?.buffDamageReduce, 0);
    if (damageResist > 0.0001 || damageReduce > 0.0001) {
      addStackStatus(statuses, {
        key: "guard",
        label: "Guard",
        kind: "positive",
        title: "Damage reduction active",
      });
    }
    if (asNumber(unitState?.buffDamagedAdd, 0) > 0.0001) {
      addStackStatus(statuses, {
        key: "vulnerable",
        label: "Vulnerable",
        kind: "negative",
        title: "Incoming damage increase active",
      });
    }

    const unitEngineGrid = unit?.engineGrid && typeof unit.engineGrid === "object"
      ? unit.engineGrid
      : null;
    if (unitEngineGrid) {
      const engineX = asNumber(unitEngineGrid.x, NaN);
      const engineY = asNumber(unitEngineGrid.y, NaN);
      const unitSide = String(unit?.side || "attacker");
      if (Number.isFinite(engineX) && Number.isFinite(engineY)) {
        rawAreaEffects.forEach((areaEffect) => {
          const areaType = String(areaEffect?.type || "").toLowerCase();
          const areaSide = String(areaEffect?.side || unitSide);
          const areaSpellGid = Math.trunc(asNumber(areaEffect?.spellGid, NaN));
          const isBisonAura = areaType.includes("bison")
            || (Number.isFinite(areaSpellGid) && BISON_AURA_SPELL_GIDS.has(areaSpellGid));
          if (!isBisonAura || areaSide !== unitSide) return;
          const center = areaEffect?.engineGrid || areaEffect?.auraCenter || {};
          const centerX = asNumber(center?.x, NaN);
          const centerY = asNumber(center?.y, NaN);
          const range = Math.max(0, asNumber(areaEffect?.range ?? areaEffect?.auraRange, 0));
          if (!Number.isFinite(centerX) || !Number.isFinite(centerY) || range <= 0) return;
          const dx = engineX - centerX;
          const dy = engineY - centerY;
          if (((dx * dx) + (dy * dy)) <= (range * range)) {
            addStackStatus(statuses, {
              key: "bison-aura",
              label: "Halo",
              kind: "aura",
              title: `Inside Source Spirit halo until frame ${Math.max(0, Math.floor(asNumber(areaEffect?.expiresFrame, frameNumber)))}`,
            });
          }
        });
      }
    }
    return statuses;
  };

  const projectRenderPoint = (point) => {
    if (point?.engineGrid && typeof point.engineGrid === "object") {
      const engineX = asNumber(point.engineGrid.x, NaN);
      const engineY = asNumber(point.engineGrid.y, NaN);
      if (Number.isFinite(engineX) && Number.isFinite(engineY)) {
        return ctx.projectEnginePoint({ x: engineX, y: engineY });
      }
    }
    if (point?.worldXZ && typeof point.worldXZ === "object") {
      const worldPoint = {
        x: asNumber(point.worldXZ.x, NaN),
        z: asNumber(point.worldXZ.z, NaN),
      };
      if (Number.isFinite(worldPoint.x) && Number.isFinite(worldPoint.z)) {
        if (ctx.mode === BATTLE_PROJECTION_MODES.TACTICAL) {
          const colHalf = ctx.grid.columnCount / 2;
          const rowHalf = ctx.grid.rowCount / 2;
          const offsetGridZ = asNumber(ctx.preset?.offsetGridZ, ctx.world.offsetGridZ);
          const engineX = (worldPoint.x / ctx.world.engineUnitToWorld) + colHalf;
          const engineY = (worldPoint.z / ctx.world.engineUnitToWorld) - offsetGridZ + rowHalf;
          return ctx.projectEnginePoint({ x: engineX, y: engineY });
        }
        return projectWorldPoint(worldPoint, ctx.preset, ctx.bounds);
      }
    }
    return null;
  };

  units.forEach((unit, index) => {
    const side = String(unit?.side || "attacker") === "defender" ? "defender" : "attacker";
    const rawTroopId = String(unit?.troopId || "");
    const familyId = String(unit?.familyId || "");
    const summonType = String(unit?.summonType || unit?.runtimeSummonType || "").trim();
    const summonOwnerStackId = String(unit?.summonOwnerStackId || "").trim() || null;
    const isSummoned = Boolean(summonType) || String(unit?.stackId || "").startsWith("summon:");
    const displayTroopId = resolveDisplayTroopId(unit);
    if (!displayTroopId || (troopIdSet.size && !troopIdSet.has(displayTroopId) && !isSummoned)) return;

    const count = Math.max(0, Math.floor(asNumber(unit?.count, 0)));
    const hp = Math.max(0, asNumber(unit?.hp, asNumber(unit?.state?.hp, 0)));
    const key = `${side}:${displayTroopId}`;

    if (side === "defender") {
      defenderFrameArmy[displayTroopId] = asNumber(defenderFrameArmy[displayTroopId], 0) + count;
      defenderFrameHp[displayTroopId] = asNumber(defenderFrameHp[displayTroopId], 0) + hp;
    } else {
      attackerFrameArmy[displayTroopId] = asNumber(attackerFrameArmy[displayTroopId], 0) + count;
      attackerFrameHp[displayTroopId] = asNumber(attackerFrameHp[displayTroopId], 0) + hp;
    }

    const projected = ctx.projectUnit(unit);
    const stackId = String(unit?.stackId || `${side}:${displayTroopId}:${index}`);
    poseByStackId[stackId] = projected;
    stackMetaById.set(stackId, { side, troopId: displayTroopId, projected, stackId });

    const weight = Math.max(1, count);
    const bucket = poseBuckets.get(key) || { weight: 0, x: 0, y: 0 };
    bucket.weight += weight;
    bucket.x += projected.x * weight;
    bucket.y += projected.y * weight;
    poseBuckets.set(key, bucket);

    const unitState = unit?.state || {};
    const statuses = buildStackStatuses(unitState, unit);
    if (unitState.moving || unitState.casting || unitState.targetId || statuses.length) {
      const entry = stackStatesByTroop[key] || {
        state: "idle",
        targetId: null,
        targetLabel: null,
        statuses: [],
      };
      if (unitState.casting) entry.state = "attacking";
      else if (unitState.moving && entry.state !== "attacking") entry.state = "moving";
      else if (unitState.targetId && entry.state === "idle") entry.state = "cooldown";
      if (!entry.targetId && unitState.targetId) {
        entry.targetId = String(unitState.targetId);
      }
      mergeStackStatuses(entry, statuses);
      stackStatesByTroop[key] = entry;
    }

    const render = unit?.render && typeof unit.render === "object" ? unit.render : {};
    const renderLabel = unit?.labels && typeof unit.labels === "object" ? unit.labels : {};
    const anchorProjected = projectRenderPoint(render?.billboardAnchor) || projected;
    const visualSlots = Array.isArray(render?.visualSlots)
      ? render.visualSlots
        .map((slot, slotIndex) => {
          const slotProjected = projectRenderPoint(slot);
          if (!slotProjected) return null;
          return {
            key: `${stackId}:slot:${slotIndex}`,
            x: slotProjected.x,
            y: slotProjected.y,
            index: Number(slot?.index ?? slotIndex),
          };
        })
        .filter(Boolean)
      : [];

    const stackState = {
      state: unitState.casting
        ? "attacking"
        : unitState.moving
          ? "moving"
          : unitState.targetId
            ? "cooldown"
            : "idle",
      targetId: unitState.targetId ? String(unitState.targetId) : null,
      targetLabel: null,
      statuses,
    };
    stackStatesByStackId[stackId] = stackState;

    renderStacks.push({
      key: `${side}:${displayTroopId}:${stackId}`,
      stackId,
      side,
      troopId: displayTroopId,
      rawTroopId,
      familyId: familyId || null,
      summonType: summonType || null,
      summonOwnerStackId,
      isSummoned,
      count,
      x: anchorProjected.x,
      y: anchorProjected.y,
      visualSlots,
      labels: {
        logicalCount: String(renderLabel.logicalCount || ""),
        visualProxyCount: Number.isFinite(Number(renderLabel.visualProxyCount))
          ? Number(renderLabel.visualProxyCount)
          : visualSlots.length,
      },
      startup: {
        visible: Boolean(render?.startup?.visible ?? true),
        revealProgress: Number.isFinite(Number(render?.startup?.revealProgress))
          ? Number(render.startup.revealProgress)
          : 1,
        justRevealed: Boolean(render?.startup?.justRevealed),
      },
      render,
    });
  });

  const poseByTroop = {};
  poseBuckets.forEach((bucket, key) => {
    poseByTroop[key] = {
      x: bucket.weight > 0 ? (bucket.x / bucket.weight) : 50,
      y: bucket.weight > 0 ? (bucket.y / bucket.weight) : 50,
    };
  });

  Object.entries(poseByStackId).forEach(([stackId, pose]) => {
    if (pose) contextLastPoseByStackId.set(String(stackId), pose);
  });
  Object.entries(poseByTroop).forEach(([key, pose]) => {
    if (pose) contextLastPoseByTroopKey.set(String(key), pose);
  });

  Object.entries(stackStatesByTroop).forEach(([key, entry]) => {
    const targetStack = entry.targetId ? stackMetaById.get(String(entry.targetId)) : null;
    if (targetStack) {
      entry.targetLabel = `${targetStack.side}:${targetStack.troopId}`;
    }
    stackStatesByTroop[key] = entry;
  });
  Object.entries(stackStatesByStackId).forEach(([stackId, entry]) => {
    const targetStack = entry.targetId ? stackMetaById.get(String(entry.targetId)) : null;
    if (targetStack) {
      entry.targetLabel = `${targetStack.side}:${targetStack.troopId}`;
    }
    stackStatesByStackId[stackId] = entry;
  });

  const attackEvents = [];
  const targetLines = [];
  const movementVectors = [];
  const castMarkers = [];
  const areaEffects = [];
  const areaEffectKeys = new Set();
  const debugEvents = [];
  const frameActionRecords = [];
  const lastSkillCastBySourceStackId = new Map();
  const ATTACK_LIKE_EVENTS = new Set([
    "ATTACK",
    "DAMAGE",
    "APPLY_QUEUED_DAMAGE",
    "MISC_DAMAGE",
    "HERO_DAMAGE",
    "TITAN_DAMAGE",
    "WARPLANE_DAMAGE",
    "COUNTER_REFLECT",
    "LOSS",
    "KILL",
    "MISS",
  ]);
  const TARGET_ONLY_ATTACK_EVENTS = new Set([
    "LOSS",
    "KILL",
  ]);
  const CAST_LIKE_EVENTS = new Set([
    "SKILL_CAST",
    "MISC_SPELL_CAST",
    "MISC_SPELL_LAUNCH",
    "MISC_SPELL_EXECUTE",
    "MISC_SPELL_END",
    "HERO_SPELL_CAST",
    "HERO_SPELL_EXECUTE",
    "TITAN_SPELL_CAST",
    "TITAN_SPELL_EXECUTE",
    "WARPLANE_SPELL_CAST",
    "WARPLANE_SPELL_EXECUTE",
    "CAST",
    "HERO_SPELL",
    "TOWER_SPELL",
    "TITAN_LAUNCH",
    "TITAN_SPELL",
    "WARPLANE_LAUNCH",
    "WARPLANE_SPELL",
    "MISC_SPELL",
  ]);
  const TARGET_LIKE_EVENTS = new Set(["TARGET_ACQUIRE", "TARGET_SWITCH"]);
  const MOVE_LIKE_EVENTS = new Set(["MOVE", "PATH_MOVE", "DASH", "PUSH"]);
  const EFFECT_MARKER_EVENTS = new Set([
    "BUFF_APPLY",
    "MISC_BUFF_APPLY",
    "MISC_BUFF_REFRESH",
    "MISC_BUFF_REMOVE",
    "MISC_BUFF_EXPIRE",
    "BUFF_EXPIRE",
    "AURA_APPLY",
    "AURA_EXPIRE",
    "DEATH",
    "REVIVE",
    "SUMMON",
  ]);

  const resolveEffectMarkerKind = (eventType, event = {}) => {
    const sourceType = String(event?.sourceType || "").toLowerCase();
    if (eventType.includes("TITAN") || sourceType.includes("titan")) return "titan-effect";
    if (eventType.includes("WARPLANE") || sourceType.includes("warplane")) return "warplane-effect";
    if (eventType.includes("BUFF") || sourceType.includes("buff")) return "buff-effect";
    if (eventType.includes("SUMMON") || eventType.includes("REVIVE")) return "summon-effect";
    if (sourceType.includes("active_skill") || sourceType.includes("update_skill") || eventType.includes("SKILL")) return "skill-effect";
    if (sourceType.includes("spell") || eventType.includes("SPELL")) return "spell-effect";
    if (eventType.includes("LOSS") || eventType.includes("KILL") || eventType.includes("DAMAGE")) return "damage-effect";
    return "effect";
  };

  const resolveEffectMarkerLabel = (eventType, event = {}) => {
    const sourceType = String(event?.sourceType || "").trim();
    const sourceId = event?.sourceId ?? event?.spellGid ?? event?.skillGid ?? event?.inferredSkillGid ?? "";
    const parts = [humanizeToken(eventType)];
    if (sourceType) parts.push(humanizeToken(sourceType));
    if (sourceId) parts.push(String(sourceId));
    return parts.join(" - ");
  };

  const projectAreaEffect = (effect = {}) => {
    const center = effect?.engineGrid || effect?.auraCenter || null;
    if (!center || typeof center !== "object") return null;
    const centerX = asNumber(center?.x, NaN);
    const centerY = asNumber(center?.y, NaN);
    if (!Number.isFinite(centerX) || !Number.isFinite(centerY)) return null;
    const projected = ctx.projectEnginePoint({ x: centerX, y: centerY });
    const range = Math.max(0, asNumber(effect?.range ?? effect?.auraRange, 0));
    let radius = 8;
    if (range > 0) {
      const horizontal = ctx.projectEnginePoint({ x: centerX + range, y: centerY });
      const vertical = ctx.projectEnginePoint({ x: centerX, y: centerY + range });
      const dx = Math.abs(horizontal.x - projected.x);
      const dy = Math.abs(vertical.y - projected.y);
      radius = Math.max(2.5, Math.min(38, (dx + dy) / 2));
    }
    const spellGid = Math.trunc(asNumber(effect?.spellGid, NaN));
    const areaType = String(effect?.type || "").toLowerCase();
    const kind = areaType.includes("bison")
      || (Number.isFinite(spellGid) && BISON_AURA_SPELL_GIDS.has(spellGid))
      ? "bison-aura"
      : "area-effect";
    return {
      x: projected.x,
      y: projected.y,
      radius,
      side: String(effect?.side || "attacker") === "defender" ? "defender" : "attacker",
      kind,
      label: kind === "bison-aura" ? "Titan Bison Aura" : humanizeToken(areaType || "Area Effect"),
      spellGid: Number.isFinite(spellGid) ? spellGid : null,
      expiresFrame: Math.max(0, Math.floor(asNumber(effect?.expiresFrame, 0))),
    };
  };

  const pushAreaEffect = (effect = {}, index = 0) => {
    const projected = projectAreaEffect(effect);
    if (!projected) return;
    const key = [
      projected.kind,
      projected.side,
      projected.spellGid ?? "",
      Math.round(projected.x * 10),
      Math.round(projected.y * 10),
      Math.round(projected.radius * 10),
      projected.expiresFrame || index,
    ].join("|");
    if (areaEffectKeys.has(key)) return;
    areaEffectKeys.add(key);
    areaEffects.push(projected);
  };

  rawAreaEffects.forEach((effect, index) => pushAreaEffect(effect, index));

  const resolvePose = ({ side, troopId, stackId }) => {
    const stackKey = stackId ? String(stackId) : "";
    if (stackKey && poseByStackId[stackKey]) return poseByStackId[stackKey];
    if (stackKey && contextLastPoseByStackId.has(stackKey)) return contextLastPoseByStackId.get(stackKey);
    const troopKey = `${String(side || "attacker")}:${String(troopId || "")}`;
    return poseByTroop[troopKey] || contextLastPoseByTroopKey.get(troopKey) || null;
  };

  const resolveSideFallbackPose = (side, referencePoint = null) => {
    const normalizedSide = String(side || "attacker") === "defender" ? "defender" : "attacker";
    const sideStacks = [...stackMetaById.values()]
      .filter((meta) => String(meta?.side || "") === normalizedSide && meta?.projected);
    if (!sideStacks.length) return null;
    if (referencePoint) {
      let nearest = null;
      let nearestDistance = Infinity;
      sideStacks.forEach((meta) => {
        const projected = meta.projected;
        const dx = asNumber(projected.x, 50) - asNumber(referencePoint.x, 50);
        const dy = asNumber(projected.y, 50) - asNumber(referencePoint.y, 50);
        const distance = (dx * dx) + (dy * dy);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearest = projected;
        }
      });
      if (nearest) return nearest;
    }
    const totals = sideStacks.reduce((acc, meta) => ({
      x: acc.x + asNumber(meta.projected.x, 50),
      y: acc.y + asNumber(meta.projected.y, 50),
    }), { x: 0, y: 0 });
    return {
      x: totals.x / sideStacks.length,
      y: totals.y / sideStacks.length,
    };
  };

  const resolveFromToPoints = (event) => {
    const fromSide = String(event?.fromSide || event?.side || event?.sourceSide || "attacker");
    const toSide = String(event?.toSide || event?.targetSide || (fromSide === "attacker" ? "defender" : "attacker"));
    const fromStackId = event?.fromStackId || event?.stackId || event?.sourceStackId || null;
    const toStackId = event?.toStackId || event?.targetStackId || event?.targetId || null;
    const fromMeta = fromStackId ? (stackMetaById.get(String(fromStackId)) || null) : null;
    const toMeta = toStackId ? (stackMetaById.get(String(toStackId)) || null) : null;
    const fromTroopIdRaw = String(event?.fromTroopId || event?.troopId || event?.sourceTroopId || "");
    const toTroopIdRaw = String(event?.toTroopId || event?.targetTroopId || "");
    const fromTroopId = String(fromMeta?.troopId || fromTroopIdRaw || "");
    const toTroopId = String(toMeta?.troopId || toTroopIdRaw || "");
    const from = resolvePose({ side: fromSide, troopId: fromTroopId || fromTroopIdRaw, stackId: fromStackId });
    const to = resolvePose({ side: toSide, troopId: toTroopId || toTroopIdRaw, stackId: toStackId });
    return {
      fromSide,
      toSide,
      fromTroopId,
      toTroopId,
      fromTroopIdRaw,
      toTroopIdRaw,
      fromStackId: fromStackId ? String(fromStackId) : null,
      toStackId: toStackId ? String(toStackId) : null,
      fromMeta,
      toMeta,
      from,
      to,
    };
  };

  const classifyFrameAction = (eventType) => {
    if (ATTACK_LIKE_EVENTS.has(eventType)) return "attack";
    if (CAST_LIKE_EVENTS.has(eventType)) return "cast";
    if (MOVE_LIKE_EVENTS.has(eventType)) return "move";
    if (TARGET_LIKE_EVENTS.has(eventType)) return "target";
    if (EFFECT_MARKER_EVENTS.has(eventType)) return "effect";
    return "system";
  };

  const buildActionTargetList = (resolved, event) => {
    const targets = [];
    if (resolved?.toSide || resolved?.toTroopId || resolved?.toStackId) {
      targets.push({
        side: String(resolved.toSide || ""),
        troopId: String(resolved.toTroopId || ""),
        troopIdRaw: String(resolved.toTroopIdRaw || ""),
        stackId: resolved.toStackId ? String(resolved.toStackId) : "",
      });
    }
    const targetStackId = String(event?.targetStackId || event?.toStackId || "").trim();
    if (targetStackId) {
      const existing = targets.find((entry) => String(entry?.stackId || "") === targetStackId);
      if (!existing) {
        const targetMeta = stackMetaById.get(targetStackId) || {};
        targets.push({
          side: String(targetMeta?.side || resolved?.toSide || ""),
          troopId: String(targetMeta?.troopId || resolved?.toTroopId || ""),
          troopIdRaw: String(resolved?.toTroopIdRaw || ""),
          stackId: targetStackId,
        });
      }
    }
    return targets;
  };

  const applyEventStatus = (resolved, status, preferTarget = true) => {
    const candidates = preferTarget
      ? [
        { side: resolved?.toSide, troopId: resolved?.toTroopId, stackId: resolved?.toStackId },
        { side: resolved?.fromSide, troopId: resolved?.fromTroopId, stackId: resolved?.fromStackId },
      ]
      : [
        { side: resolved?.fromSide, troopId: resolved?.fromTroopId, stackId: resolved?.fromStackId },
        { side: resolved?.toSide, troopId: resolved?.toTroopId, stackId: resolved?.toStackId },
      ];
    const target = candidates.find((candidate) => candidate?.stackId || (candidate?.side && candidate?.troopId));
    if (!target) return;
    if (target.stackId) {
      const stackId = String(target.stackId);
      const entry = stackStatesByStackId[stackId] || {
        state: "idle",
        targetId: null,
        targetLabel: null,
        statuses: [],
      };
      mergeStackStatuses(entry, [status]);
      stackStatesByStackId[stackId] = entry;
    }
    if (target.side && target.troopId) {
      const key = `${String(target.side)}:${String(target.troopId)}`;
      const entry = stackStatesByTroop[key] || {
        state: "idle",
        targetId: null,
        targetLabel: null,
        statuses: [],
      };
      mergeStackStatuses(entry, [status]);
      stackStatesByTroop[key] = entry;
    }
  };

  const updateLastOfficerSkill = (event, resolved) => {
    const officerSkillId = resolveOfficerSourceSkillId(event);
    if (!officerSkillId) return null;
    const side = String(resolved?.fromSide || event?.side || event?.fromSide || event?.sourceSide || "attacker") === "defender"
      ? "defender"
      : "attacker";
    contextLastOfficerSkillBySide.set(side, officerSkillId);
    return { side, skillId: officerSkillId };
  };

  const resolveRecentOfficerSkillId = (resolved, event) => {
    const side = String(resolved?.fromSide || event?.side || event?.fromSide || event?.sourceSide || "attacker") === "defender"
      ? "defender"
      : "attacker";
    return contextLastOfficerSkillBySide.get(side) || null;
  };

  const rememberSourceForTarget = (resolved, sourcePoint) => {
    if (!resolved?.toStackId || !sourcePoint) return;
    contextLastSourceByTargetStackId.set(String(resolved.toStackId), {
      point: sourcePoint,
      fromSide: resolved.fromSide,
      fromTroopId: resolved.fromTroopId,
      fromStackId: resolved.fromStackId,
    });
  };

  const resolveRecentSourceForTarget = (resolved) => {
    if (!resolved?.toStackId) return null;
    const recent = contextLastSourceByTargetStackId.get(String(resolved.toStackId));
    return recent?.point || null;
  };

  const pushFrameActionRecord = (event, eventType, resolved) => {
    const sourceType = String(event?.sourceType || "").trim();
    const skillGid = Math.trunc(asNumber(event?.skillGid, NaN));
    const spellGid = Math.trunc(asNumber(event?.spellGid, NaN));
    const sourceId = Math.trunc(asNumber(event?.sourceId, NaN));
    const sourceGid = Math.trunc(asNumber(event?.sourceGid, NaN));
    const sourceStackId = String(resolved?.fromStackId || "").trim();
    if (eventType === "SKILL_CAST" && sourceStackId && Number.isFinite(skillGid)) {
      lastSkillCastBySourceStackId.set(sourceStackId, skillGid);
    }
    let inferredSkillGid = Math.trunc(asNumber(event?.inferredSkillGid, NaN));
    if (
      !Number.isFinite(inferredSkillGid)
      && !Number.isFinite(skillGid)
      && sourceStackId
      && (sourceType === "active_skill" || sourceType === "update_skill")
    ) {
      inferredSkillGid = Math.trunc(asNumber(lastSkillCastBySourceStackId.get(sourceStackId), NaN));
    }
    frameActionRecords.push({
      index: frameActionRecords.length,
      frame: Math.max(0, Math.floor(asNumber(event?.frame, frame?.frame))),
      phase: String(event?.phase || frame?.phase || ""),
      eventType,
      category: classifyFrameAction(eventType),
      sourceType,
      source: {
        side: String(resolved?.fromSide || event?.side || event?.fromSide || ""),
        troopId: String(resolved?.fromTroopId || ""),
        troopIdRaw: String(resolved?.fromTroopIdRaw || ""),
        stackId: String(resolved?.fromStackId || event?.stackId || event?.fromStackId || ""),
        wtype: Math.trunc(asNumber(event?.sourceWtype, resolved?.fromMeta?.wtype)),
      },
      targets: buildActionTargetList(resolved, event),
      skillGid: Number.isFinite(skillGid) ? skillGid : null,
      spellGid: Number.isFinite(spellGid) ? spellGid : null,
      sourceId: Number.isFinite(sourceId) ? sourceId : null,
      sourceGid: Number.isFinite(sourceGid) ? sourceGid : null,
      inferredSkillGid: Number.isFinite(inferredSkillGid) ? inferredSkillGid : null,
      damage: asNumber(event?.damage, 0),
      queuedDamage: asNumber(event?.queuedDamage, 0),
      deadUnits: asNumber(event?.deadUnits, 0),
      deadTroops: asNumber(event?.deadTroops, 0),
      blocked: Boolean(event?.blocked || event?.isBlock),
      critical: Boolean(event?.critical || event?.isCritical),
      miss: Boolean(event?.miss || eventType === "MISS"),
      isEvade: Boolean(event?.isEvade),
      banSkill: Boolean(event?.banSkill),
      misfire: Boolean(event?.misfire),
      move: {
        fromEngineX: asNumber(event?.fromEngineX, NaN),
        fromEngineY: asNumber(event?.fromEngineY, NaN),
        toEngineX: asNumber(event?.toEngineX, NaN),
        toEngineY: asNumber(event?.toEngineY, NaN),
      },
    });
  };

  events.forEach((event) => {
    const eventType = String(event?.type || "").toUpperCase();
    const spellGid = Math.trunc(asNumber(event?.spellGid, NaN));
    const resolved = resolveFromToPoints(event);
    pushFrameActionRecord(event, eventType, resolved);
    if (event?.misfire) {
      applyEventStatus(resolved, {
        key: "misfire",
        label: "Misfire",
        kind: "negative",
        title: "Misfire triggered this frame",
      }, true);
    }
    if (event?.banSkill) {
      applyEventStatus(resolved, {
        key: "skill-ban",
        label: "Skill Ban",
        kind: "negative",
        title: "Skill casting blocked",
      }, true);
    }
    if (CAST_LIKE_EVENTS.has(eventType)) {
      updateLastOfficerSkill(event, resolved);
    }
    if (ATTACK_LIKE_EVENTS.has(eventType)) {
      const { from, to } = resolved;
      const markerTarget = to || from;
      const hasExplicitTargetOnly = TARGET_ONLY_ATTACK_EVENTS.has(eventType);
      const officerSourceSkillId = (!from && to)
        ? (resolveOfficerSourceSkillId(event) || resolveRecentOfficerSkillId(resolved, event))
        : null;
      const officerSourceMarker = officerSourceSkillId
        ? buildOfficerSourceMarker({
          skillId: officerSourceSkillId,
          side: resolved.fromSide,
          targetPoint: to,
        })
        : null;
      const fallbackFrom = (!from && to && !officerSourceMarker)
        ? (resolveRecentSourceForTarget(resolved) || resolveSideFallbackPose(resolved.fromSide, to))
        : null;
      const lineFrom = from || officerSourceMarker || fallbackFrom;
      if (markerTarget && hasExplicitTargetOnly && !lineFrom) {
        castMarkers.push({
          x: markerTarget.x,
          y: markerTarget.y,
          side: resolved.toSide || resolved.fromSide || "attacker",
          kind: resolveEffectMarkerKind(eventType, event),
          label: resolveEffectMarkerLabel(eventType, event),
          radius: eventType === "KILL" || eventType === "LOSS" ? 0.9 : 1.05,
        });
      }
      if (lineFrom && to) {
        if (!officerSourceMarker) {
          rememberSourceForTarget(resolved, lineFrom);
        }
        attackEvents.push({
          fromSide: resolved.fromSide,
          fromTroopId: resolved.fromTroopId,
          fromStackId: resolved.fromStackId,
          toSide: resolved.toSide,
          toTroopId: resolved.toTroopId,
          toStackId: resolved.toStackId,
          x1: lineFrom.x,
          y1: lineFrom.y,
          x2: to.x,
          y2: to.y,
          inferredSource: Boolean(fallbackFrom),
          sourceMarker: officerSourceMarker,
          sourceSkillId: officerSourceSkillId,
          miss: Boolean(event?.miss),
          blocked: Boolean(event?.blocked),
          critical: Boolean(event?.critical || event?.isCritical),
          damage: asNumber(event?.damage, 0),
        });
        const line = buildTargetLine({ from: lineFrom, to, style: eventType === "DAMAGE" ? "damage" : "attack" });
        if (line) targetLines.push(line);
      } else if (to && resolved.toTroopId) {
        attackEvents.push({
          fromSide: resolved.fromSide || "unknown",
          fromTroopId: resolved.fromTroopId || "",
          fromStackId: resolved.fromStackId,
          toSide: resolved.toSide || "unknown",
          toTroopId: resolved.toTroopId || "",
          toStackId: resolved.toStackId,
          miss: eventType === "MISS" || Boolean(event?.miss),
          blocked: Boolean(event?.blocked),
          critical: Boolean(event?.critical || event?.isCritical),
          damage: asNumber(event?.damage, 0),
        });
      }
      debugEvents.push({
        type: `${event?.phase || "Update"}:${eventType}`,
        side: resolved.fromSide || "unknown",
        troopId: resolved.fromTroopId || "unknown",
        targetId: `${resolved.toSide || "unknown"}:${resolved.toTroopId || "unknown"}`,
        damage: asNumber(event?.damage, 0),
      });
      return;
    }

    if (CAST_LIKE_EVENTS.has(eventType)) {
      const { from, to } = resolved;
      const isEnergyGuard = Number.isFinite(spellGid) && spellGid === ENERGY_GUARD_SPELL_GID;
      const officerCast = updateLastOfficerSkill(event, resolved);
      const markerTarget = to || resolveSideFallbackPose(resolved.toSide, null) || from;
      const officerSourceMarker = officerCast?.skillId
        ? buildOfficerSourceMarker({
          skillId: officerCast.skillId,
          side: officerCast.side,
          targetPoint: markerTarget,
        })
        : null;
      if (event?.auraCenter && Number.isFinite(spellGid) && BISON_AURA_SPELL_GIDS.has(spellGid)) {
        pushAreaEffect({
          type: "bison_aura",
          side: resolved.fromSide,
          spellGid,
          engineGrid: event.auraCenter,
          range: event?.auraRange,
        }, areaEffects.length);
      }
      const markerKind = isEnergyGuard
        ? (eventType === "MISC_SPELL_END" ? "energy-guard-end" : "energy-guard-start")
        : resolveEffectMarkerKind(eventType, event);
      const markerLabel = resolveEffectMarkerLabel(eventType, event);
      if (from) {
        castMarkers.push({
          x: from.x,
          y: from.y,
          side: resolved.fromSide,
          kind: markerKind,
          label: markerLabel,
          radius: markerKind === "titan-effect" || markerKind === "warplane-effect" ? 1.45 : 1.15,
        });
      }
      if (officerSourceMarker) {
        attackEvents.push({
          fromSide: officerCast.side,
          fromTroopId: "",
          fromStackId: null,
          toSide: resolved.toSide,
          toTroopId: resolved.toTroopId,
          toStackId: resolved.toStackId,
          x1: officerSourceMarker.x,
          y1: officerSourceMarker.y,
          x2: markerTarget?.x || officerSourceMarker.x,
          y2: markerTarget?.y || officerSourceMarker.y,
          sourceMarker: officerSourceMarker,
          sourceSkillId: officerCast.skillId,
          inferredSource: true,
          miss: false,
          blocked: false,
          critical: false,
          damage: 0,
        });
      }
      if (to && markerKind) {
        castMarkers.push({
          x: to.x,
          y: to.y,
          side: resolved.toSide || resolved.fromSide,
          kind: markerKind,
          label: markerLabel,
          radius: markerKind === "titan-effect" || markerKind === "warplane-effect" ? 1.45 : 1.15,
        });
      }
      const line = buildTargetLine({ from, to, style: "skill" });
      if (line) targetLines.push(line);
      debugEvents.push({
        type: `${event?.phase || "Update"}:${eventType}${isEnergyGuard ? ":ENERGY_GUARD" : ""}`,
        side: resolved.fromSide || "unknown",
        troopId: resolved.fromTroopId || "unknown",
        targetId: `${resolved.toSide || "unknown"}:${resolved.toTroopId || "unknown"}`,
        damage: 0,
      });
      return;
    }

    if (MOVE_LIKE_EVENTS.has(eventType)) {
      let from = null;
      let to = null;
      if (Number.isFinite(asNumber(event?.fromEngineX, NaN)) && Number.isFinite(asNumber(event?.fromEngineY, NaN))) {
        from = ctx.projectEnginePoint({ x: event.fromEngineX, y: event.fromEngineY });
      }
      if (Number.isFinite(asNumber(event?.toEngineX, NaN)) && Number.isFinite(asNumber(event?.toEngineY, NaN))) {
        to = ctx.projectEnginePoint({ x: event.toEngineX, y: event.toEngineY });
      }
      if (!from || !to) {
        const pose = resolvePose({
          side: event?.side || event?.fromSide || "attacker",
          troopId: event?.troopId || event?.fromTroopId || "",
          stackId: event?.stackId || event?.fromStackId || null,
        });
        from = from || pose;
        to = to || pose;
      }
      if (from && to) {
        movementVectors.push({
          x1: from.x,
          y1: from.y,
          x2: to.x,
          y2: to.y,
          side: String(event?.side || "attacker"),
        });
      }
      debugEvents.push({
        type: `${event?.phase || "PathUpdate"}:${eventType}`,
        side: String(event?.side || "unknown"),
        troopId: String(event?.troopId || "unknown"),
        targetId: `${asNumber(event?.toEngineX, 0).toFixed(1)},${asNumber(event?.toEngineY, 0).toFixed(1)}`,
        damage: 0,
      });
      return;
    }

    if (TARGET_LIKE_EVENTS.has(eventType)) {
      const line = buildTargetLine({ from: resolved.from, to: resolved.to, style: "target" });
      if (line) targetLines.push(line);
      debugEvents.push({
        type: `${event?.phase || "Update"}:${eventType}`,
        side: resolved.fromSide || "unknown",
        troopId: resolved.fromTroopId || "unknown",
        targetId: `${resolved.toSide || "unknown"}:${resolved.toTroopId || "unknown"}`,
        damage: 0,
      });
      return;
    }

    if (EFFECT_MARKER_EVENTS.has(eventType)) {
      const marker = resolved.to || resolved.from;
      if (marker) {
        castMarkers.push({
          x: marker.x,
          y: marker.y,
          side: resolved.toSide || resolved.fromSide || "attacker",
          kind: resolveEffectMarkerKind(eventType, event),
          label: resolveEffectMarkerLabel(eventType, event),
          radius: 1.1,
        });
      }
      debugEvents.push({
        type: `${event?.phase || "Update"}:${eventType}`,
        side: resolved.toSide || resolved.fromSide || "unknown",
        troopId: resolved.toTroopId || resolved.fromTroopId || "unknown",
        targetId: "",
        damage: asNumber(event?.damage, 0),
      });
      return;
    }

    if (resolved.from && resolved.to) {
      const line = buildTargetLine({ from: resolved.from, to: resolved.to, style: "event" });
      if (line) targetLines.push(line);
    } else {
      const marker = resolved.to || resolved.from;
      if (marker) {
        castMarkers.push({
          x: marker.x,
          y: marker.y,
          side: resolved.toSide || resolved.fromSide || "attacker",
          kind: resolveEffectMarkerKind(eventType, event),
          label: resolveEffectMarkerLabel(eventType, event),
          radius: 1.05,
        });
      }
    }
    debugEvents.push({
      type: `${event?.phase || "Frame"}:${event?.type || "EVENT"}`,
      side: resolved.fromSide || resolved.toSide || "system",
      troopId: resolved.fromTroopId || resolved.toTroopId || "all",
      targetId: `${resolved.toSide || "unknown"}:${resolved.toTroopId || "unknown"}`,
      damage: asNumber(event?.damage, 0),
    });
  });

  const frameActionLog = [];
  const actionGroupMap = new Map();
  frameActionRecords.forEach((record) => {
    const sourceStackId = String(record?.source?.stackId || "").trim();
    const sourceTroopId = String(record?.source?.troopId || "");
    const sourceSide = String(record?.source?.side || "");
    const sourceType = String(record?.sourceType || "");
    const key = [
      String(record?.phase || ""),
      String(record?.eventType || ""),
      sourceSide,
      sourceStackId || sourceTroopId,
      sourceType,
      String(record?.skillGid ?? ""),
      String(record?.inferredSkillGid ?? ""),
      String(record?.spellGid ?? ""),
    ].join("|");
    let group = actionGroupMap.get(key);
    if (!group) {
      group = {
        ...record,
        targets: [],
        eventCount: 0,
        totalDamage: 0,
        totalQueuedDamage: 0,
        totalDeadUnits: 0,
        totalDeadTroops: 0,
      };
      actionGroupMap.set(key, group);
      frameActionLog.push(group);
    }
    group.eventCount += 1;
    group.totalDamage += asNumber(record?.damage, 0);
    group.totalQueuedDamage += asNumber(record?.queuedDamage, 0);
    group.totalDeadUnits += asNumber(record?.deadUnits, 0);
    group.totalDeadTroops += asNumber(record?.deadTroops, 0);
    (Array.isArray(record?.targets) ? record.targets : []).forEach((target) => {
      const targetKey = [
        String(target?.side || ""),
        String(target?.troopId || ""),
        String(target?.stackId || ""),
      ].join("|");
      if (!targetKey.trim()) return;
      const exists = group.targets.some((entry) => (
        String(entry?.side || "") === String(target?.side || "")
        && String(entry?.troopId || "") === String(target?.troopId || "")
        && String(entry?.stackId || "") === String(target?.stackId || "")
      ));
      if (!exists) group.targets.push(target);
    });
  });

  return {
    projectionMode: ctx.mode,
    projectionPreset: ctx.preset,
    attackerFrameArmy,
    defenderFrameArmy,
    attackerFrameHp,
    defenderFrameHp,
    poseByTroop,
    poseByStackId,
    stackStatesByTroop,
    stackStatesByStackId,
    renderStacks,
    attackEvents,
    targetLines,
    movementVectors,
    castMarkers,
    areaEffects,
    debugEvents,
    frameActionLog,
  };
}
